import { useRef, useState } from "react";
import type { NormalizedQuad, TopProfile } from "../lib/api/types";
import { Button } from "./Button";
import { CloseIcon } from "./icons";
import { QuadEditor, type EditorDoor } from "./QuadEditor";

// Fullscreen EDITING surface (not a lightbox): the same QuadEditor, full-viewport, with pinch-zoom.
// Corner-dragging is the hardest interaction in the product and on a phone it happens on a ~200px
// photo where a fingertip covers the handle — here the photo fills the screen and zooms to 4×.
//
// Zoom model: a CSS transform (translate + scale, origin 0 0) on a stage-filling content layer.
// QuadEditor needs NO changes to work inside it — its pointer math normalizes against the wrap's
// getBoundingClientRect(), which reports transformed (scaled) geometry, so drags stay exact at any
// zoom. Gestures: two-finger pinch (zoom + pan), one-finger drag on non-handle areas (pan when
// zoomed), double-tap to toggle 1×/2.5×, and +/− buttons for mouse/discoverability. Pointers that
// start on a drag handle are ignored here — the handle's own capture-based drag owns them.

interface View {
  scale: number;
  tx: number;
  ty: number;
}

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_MS = 320;
const DOUBLE_TAP_SLOP = 40;
const TAP_ZOOM = 2.5;

type Gesture =
  | { kind: "pan"; startX: number; startY: number; startTx: number; startTy: number }
  | { kind: "pinch"; startDist: number; startScale: number; startMidX: number; startMidY: number; startTx: number; startTy: number };

export function FullscreenEditor({
  photoUrl, backdropUrl, doors, activeDoorIds, busy,
  onSelectDoor, onQuadChange, onQuadCommit, onProfileChange, onProfileCommit, onClose,
}: {
  photoUrl: string;
  backdropUrl: string;
  doors: EditorDoor[];
  activeDoorIds: string[];
  busy?: boolean;
  onSelectDoor: (id: string) => void;
  onQuadChange: (id: string, quad: NormalizedQuad) => void;
  onQuadCommit: (id: string, quad: NormalizedQuad) => void;
  onProfileChange: (id: string, profile: TopProfile | null) => void;
  onProfileCommit: (id: string, profile: TopProfile | null) => void;
  onClose: () => void;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const [view, setViewState] = useState<View>({ scale: 1, tx: 0, ty: 0 });
  const viewRef = useRef(view); // gesture math reads this (state is only for rendering)
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<Gesture | null>(null);
  const moved = useRef(false);
  const lastTap = useRef<{ t: number; x: number; y: number } | null>(null);

  // Clamp so the (scaled) content always covers the viewport: with origin 0,0 the translate range
  // is [-(scale-1)·size, 0] per axis. scale 1 snaps home so the layout is pixel-stable when unzoomed.
  const clamp = (v: View): View => {
    const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.scale));
    const r = stageRef.current?.getBoundingClientRect();
    if (!r || scale === 1) return { scale, tx: 0, ty: 0 };
    return {
      scale,
      tx: Math.min(0, Math.max(-(scale - 1) * r.width, v.tx)),
      ty: Math.min(0, Math.max(-(scale - 1) * r.height, v.ty)),
    };
  };
  const apply = (v: View) => {
    const c = clamp(v);
    viewRef.current = c;
    setViewState(c);
  };

  // (Re)baseline the active gesture from the pointers currently down — called when a pointer is
  // added or removed, so pinch→pan and pan→pinch transitions never jump.
  const rebase = () => {
    const pts = [...pointers.current.values()];
    const v = viewRef.current;
    if (pts.length >= 2) {
      const [a, b] = pts;
      gesture.current = {
        kind: "pinch",
        startDist: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)),
        startScale: v.scale,
        startMidX: (a.x + b.x) / 2,
        startMidY: (a.y + b.y) / 2,
        startTx: v.tx,
        startTy: v.ty,
      };
    } else if (pts.length === 1) {
      gesture.current = { kind: "pan", startX: pts[0].x, startY: pts[0].y, startTx: v.tx, startTy: v.ty };
    } else {
      gesture.current = null;
    }
  };

  // Zoom by a factor keeping the stage point (cx, cy) visually fixed.
  const zoomAt = (cx: number, cy: number, targetScale: number) => {
    const v = viewRef.current;
    const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, targetScale));
    const k = scale / v.scale;
    apply({ scale, tx: cx - k * (cx - v.tx), ty: cy - k * (cy - v.ty) });
  };
  const zoomStep = (factor: number) => {
    const r = stageRef.current?.getBoundingClientRect();
    if (!r) return;
    zoomAt(r.width / 2, r.height / 2, viewRef.current.scale * factor);
  };

  const stagePoint = (e: React.PointerEvent) => {
    const r = stageRef.current?.getBoundingClientRect();
    return { x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0) };
  };

  function onPointerDown(e: React.PointerEvent) {
    // A pointer that lands on a drag handle belongs to the handle (capture-based drag) — tracking
    // it here would fight the drag with a pan.
    if ((e.target as Element).closest?.(".rg-corner-handle, .rg-sub-handle, button")) return;
    pointers.current.set(e.pointerId, stagePoint(e));
    if (pointers.current.size === 1) moved.current = false;
    rebase();
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, stagePoint(e));
    const g = gesture.current;
    if (!g) return;
    if (g.kind === "pinch") {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.max(1, Math.hypot(b.x - a.x, b.y - a.y));
      const midX = (a.x + b.x) / 2;
      const midY = (a.y + b.y) / 2;
      const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, g.startScale * (dist / g.startDist)));
      const k = scale / g.startScale;
      moved.current = true;
      apply({ scale, tx: midX - k * (g.startMidX - g.startTx), ty: midY - k * (g.startMidY - g.startTy) });
    } else {
      const p = pointers.current.get(e.pointerId)!;
      const dx = p.x - g.startX;
      const dy = p.y - g.startY;
      if (Math.abs(dx) + Math.abs(dy) > 3) moved.current = true;
      if (viewRef.current.scale > 1) apply({ scale: viewRef.current.scale, tx: g.startTx + dx, ty: g.startTy + dy });
    }
  }

  function onPointerEnd(e: React.PointerEvent) {
    if (!pointers.current.delete(e.pointerId)) return;
    // Double-tap (touch) toggles 1× ↔ 2.5× anchored at the tap. Only a clean tap counts: no pan
    // movement, no second finger still down.
    if (!moved.current && pointers.current.size === 0 && e.type !== "pointercancel") {
      const p = stagePoint(e);
      const now = performance.now();
      const prev = lastTap.current;
      if (prev && now - prev.t < DOUBLE_TAP_MS && Math.abs(p.x - prev.x) < DOUBLE_TAP_SLOP && Math.abs(p.y - prev.y) < DOUBLE_TAP_SLOP) {
        lastTap.current = null;
        if (viewRef.current.scale > 1.01) apply({ scale: 1, tx: 0, ty: 0 });
        else zoomAt(p.x, p.y, TAP_ZOOM);
      } else {
        lastTap.current = { t: now, x: p.x, y: p.y };
      }
    }
    rebase();
  }

  const chromeBtn: React.CSSProperties = {
    width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center",
    background: "rgba(255,255,255,0.12)", color: "#fff", border: "none", borderRadius: 10,
    cursor: "pointer", backdropFilter: "blur(4px)",
  };

  return (
    <div
      className="rg-fs-editor"
      data-testid="fullscreen-editor"
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15,23,42,0.94)", touchAction: "none", overscrollBehavior: "contain" }}
    >
      <div
        ref={stageRef}
        style={{ position: "absolute", inset: 0, overflow: "hidden" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
      >
        <div
          style={{
            width: "100%", height: "100%",
            display: "flex", alignItems: "center", justifyContent: "center",
            transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`,
            transformOrigin: "0 0",
            willChange: "transform",
          }}
        >
          <QuadEditor
            photoUrl={photoUrl}
            backdropUrl={backdropUrl}
            doors={doors}
            activeDoorIds={activeDoorIds}
            busy={busy}
            onSelectDoor={onSelectDoor}
            onQuadChange={onQuadChange}
            onQuadCommit={onQuadCommit}
            onProfileChange={onProfileChange}
            onProfileCommit={onProfileCommit}
          />
        </div>
      </div>

      {/* Chrome floats above the stage (siblings, so stage gestures never see these pointers). */}
      <button type="button" title="Close" aria-label="Close full screen" onClick={onClose} style={{ ...chromeBtn, position: "absolute", top: 16, right: 16 }}>
        <CloseIcon size={22} />
      </button>

      <div style={{ position: "absolute", right: 16, bottom: "calc(84px + env(safe-area-inset-bottom))", display: "flex", flexDirection: "column", gap: 8 }}>
        <button type="button" aria-label="Zoom in" onClick={() => zoomStep(1.5)} style={{ ...chromeBtn, fontSize: 22, fontWeight: 600 }}>+</button>
        <button type="button" aria-label="Zoom out" onClick={() => zoomStep(1 / 1.5)} style={{ ...chromeBtn, fontSize: 22, fontWeight: 600 }}>−</button>
      </div>

      <div
        style={{
          position: "absolute", left: 0, right: 0, bottom: "calc(16px + env(safe-area-inset-bottom))",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 8, pointerEvents: "none",
        }}
      >
        <div className="rg-fs-hint" style={{ color: "rgba(255,255,255,0.75)", fontSize: 12, textAlign: "center", padding: "0 16px" }}>
          Pinch or double-tap to zoom · drag the corners to fit your door
        </div>
        <Button variant="primary" onClick={onClose} style={{ pointerEvents: "auto", minWidth: 140 }}>
          Done
        </Button>
      </div>
    </div>
  );
}
